import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import type {
  CheckboxBlockStateType,
  CheckboxComponentType,
  ComputedCheckboxBlockStateType,
} from '@unifyapps/defs/types/blocks';
import { memo, useMemo } from 'react';
import { Checkbox as BaseCheckbox } from '@unifyapps/ui/components/Checkbox';
import { Stack } from '@unifyapps/ui/components/Stack';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import { Typography } from '@unifyapps/ui/components/Typography';
import useCustomStyles from '@unifyapps/carbon/utils/useCustomStyles';
import { useGetAddOnsStyle } from '../common/fields/BooleanField/hooks/useGetAddOnsStyle';
import { useSetDefaultValue } from '../common/fields/BooleanField/hooks/useSetDefaultValue';

function Checkbox({
  attributesAndEventListeners,
  events,
  computedBlockState,
  blockState,
  updateBlockState,
  dndRef,
  block,
  dataSourceIds,
  getGlobalStoreContext,
}: BlockComponentProps<
  CheckboxComponentType,
  CheckboxBlockStateType,
  ComputedCheckboxBlockStateType
>) {
  const { content, appearance, value } = computedBlockState;
  const { label, description, help, defaultValue } = content;
  const { emitEvent } = useBlockEvents(events);
  const { neumorphicStyle } = useCustomStyles({ customCSS: block.additional?.customCSS });
  const { resolvedValue } = useSetDefaultValue<CheckboxBlockStateType>({
    defaultValue,
    uncomputedDefaultValue: blockState.content.defaultValue,
    updateBlockState,
    dataSourceIds,
    getGlobalStoreContext,
    value,
  });

  const { labelStyles, descriptionStyles, helpStyles } = useGetAddOnsStyle(content.addOns);

  const onChange = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.checked;
    updateBlockState((draft) => {
      draft.value = nextValue;
    });

    void emitEvent({
      eventType: EventTypeEnum.OnCheckChange,
      actionContext: { value: nextValue },
    });
  });

  const _label = useMemo(() => {
    if (label) {
      return (
        <Typography
          className={labelStyles.color}
          variant={labelStyles.variant}
          weight={labelStyles.weight}
        >
          {label}
        </Typography>
      );
    }
    return null;
  }, [label, labelStyles]);

  return (
    <Stack {...attributesAndEventListeners} className="gap-y-sm" ref={dndRef}>
      <Stack className="gap-xxs" direction="column">
        <BaseCheckbox
          checked={resolvedValue}
          className="items-center"
          disabled={appearance?.disabled}
          label={_label}
          neumorphicStyle={neumorphicStyle}
          onChange={onChange}
          size={appearance?.size}
        />
        {description ? (
          <Typography
            className={descriptionStyles.color}
            variant={descriptionStyles.variant}
            weight={descriptionStyles.weight}
          >
            {description}
          </Typography>
        ) : null}
      </Stack>
      {help ? (
        <Typography
          className={helpStyles.color}
          variant={helpStyles.variant}
          weight={helpStyles.weight}
        >
          {help}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default memo(Checkbox);
